import { SWRConfig } from 'swr';
import SEO from '@seo/index';
import { useRouter } from 'next/router';
import StoreListing from '@cont/StoreListing/StoreListing';
import NewGoalLayout from '@layout/NewGoalLayout/NewGoalLayout';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import { fetchGoalInfo, fetchUserProfileInfo } from '@api/fetchers/store';
import Loader from '@cont/Combat/Common/Loader';
import fetchPageMetaInfo from '@api/fetchers/pageMetaInfo';
import { useEffect } from 'react';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import useUserProfileInfo from '@api/hooks/goal/useUserProfileInfo';
import useStoreFilters from '@api/hooks/goal/useStoreFilters';
import useGoalInfo from '@api/hooks/goal/useGoalInfo';
import { emptyObject } from '@constants/empty';

const Store = ({ goalDetails, pageMetaInfo }) => {
  const {
    query: { goalUID },
    replace
  } = useRouter();
  const { slug: goalSlug } = goalDetails ?? {};
  const canonicalURL = `https://unacademy.com/goal/${goalSlug}/${goalUID}/store`;
  const { goalInfo: { ogImage } = emptyObject } =
    useGoalInfo(goalUID) ?? emptyObject;

  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { userProfileInfo, mutate: mutateUserProfile } =
    useUserProfileInfo(goalUID);
  const {
    storeFilters,
    mutate: mutateStoreFilters,
    isValidating
  } = useStoreFilters(goalUID);

  useEffect(() => {
    if (isLoggedIn) {
      mutateUserProfile();
      mutateStoreFilters();
    }
  }, [isLoggedIn, mutateUserProfile, mutateStoreFilters]);

  useEffect(() => {
    if (userProfileInfo && !userProfileInfo.showStoreTab) {
      replace('/goal/[goalSlug]/[goalUID]', `/goal/${goalSlug}/${goalUID}`);
    }
  }, [userProfileInfo, goalSlug, goalUID, replace]);

  const { title: metaTitle, description: metaDescription } = pageMetaInfo;
  const imageURL =
    ogImage ||
    'https://static.uacdn.net/static/dist/c76c2e18c7980818a913f9467c74c567.png';

  return (
    <>
      <SWRConfig
        value={{
          revalidateOnMount: false,
          dedupingInterval: 60 * 1000,
          fallback: {
            [`v1/topology/users/${goalUID}/self/`]: goalDetails
          }
        }}
      >
        <SEO
          title={metaTitle}
          description={metaDescription}
          openGraph={{
            title: metaTitle,
            description: metaDescription,
            images: [
              {
                url: imageURL
              }
            ],
            url: canonicalURL
          }}
        />
        {!isValidating && storeFilters ? (
          <StoreListing filtersData={storeFilters} />
        ) : (
          <Loader />
        )}
      </SWRConfig>
    </>
  );
};

export default Store;

Store.pageInfo = {
  isMWebSupported: true,
  name: 'Store Listing Page',
  layoutOptions: {
    hideFreshbot: true,
    hideFooter: true,
    hideIntercom: true,
    showPlusNav: true,
    hideLearnerMinutes: true,
    disableDarkMode: true,
    whiteBackground: true
  }
};

export const getStaticProps = async ({ params: query }) => {
  const { goalUID } = query;

  const [goalDetails, metaInfo, userProfile] = await Promise.all([
    fetchGoalInfo(goalUID),
    fetchPageMetaInfo(Store.pageInfo.name, goalUID),
    fetchUserProfileInfo(goalUID)
  ]);

  return {
    props: {
      goalDetails,
      pageMetaInfo: {
        ...metaInfo,
        showStoreTab: userProfile?.showStoreTab || false
      }
    },
    revalidate: 1 * 60
  };
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

Store.Layout = NewGoalLayout;
Store.Wrapper = PageReduxProvider;
